﻿// replace jquery event handlers with commented lines above when jquery events are removed completely from rustig
// Native events do not support namespacing, ensure all module/utility specific events still have the correct name when doing so

import $ from 'jquery';
import _ from 'underscore';
import { Element } from "../utilities/element";
import { SuppressesEvent } from "../utilities/suppressesevent";
import { StringExtensions, IRustigEvent } from "../utilities/extensions";
import { ToggleShow } from '../utilities/display';
import EditState from '../utilities/readonly';
import DropDown from './dropdown';
import { IPopOverHTMLElement } from './popover';

export class DateRangeField {
  public static get wireup(): string { return '[data-daterange-input]'; }

  private DEFAULTS: IDateRangeFieldOptions = {
    daterangeKey: null,
    allowsEmpty: true
  }

  private options: IDateRangeFieldOptions;
  private _valueElement: HTMLInputElement;
  private _userFacingElement: IPopOverHTMLElement;
  private popover: Popover;
  private start: HTMLInputElement;
  private end: HTMLInputElement;

  public get element(): HTMLElement { return this._valueElement; }
  public get userFacingElement(): IPopOverHTMLElement { return this._userFacingElement; }
  public get valueElement(): HTMLInputElement { return this._valueElement; }

  public constructor(element: HTMLInputElement, options?: IDateRangeFieldOptions) {
    if (!element)
      throw new Error('You must provide an element to DateRangeField');

    try {
      $(element).data('r-is-constructing', true);
      this._valueElement = element;
      this.options = _.extend({}, this.DEFAULTS, this.getOptionsFromElement(), options);

      let root = this.valueElement.closest('[data-daterange]');
      this._userFacingElement = root.querySelector('[data-daterange-display]');
      this.start = root.querySelector('[data-start="hidden"]');
      this.end = root.querySelector('[data-end="hidden"]');

      this.setUpPopover();
      this.setUpUserFacingElement();

      new SuppressesEvent().execute('change', this.valueElement, () => {
        this.setValue(new NullableDate(this.start.value), new NullableDate(this.end.value));
      });
    }
    finally {
      $(element).data('r-is-constructing', false);
    }
  }

  public static load(element: HTMLElement, options?: IDateRangeFieldOptions): DateRangeField {
    let dateRangeElement = element as IDateRangeElement;
    if (!dateRangeElement.dateRangeField)
      dateRangeElement.dateRangeField = new DateRangeField(dateRangeElement, options);

    return dateRangeElement.dateRangeField;
  }

  public value(value?: any): any | void {
    if (value === undefined)
      return this.valueElement.value;

    let start = null;
    let end = null;

    if (value.indexOf('thru') !== -1) {
      let parts = value.split('thru');
      start = parts[0];
      end = parts[1];
    }

    if (this.options.allowsEmpty || (start || end)) {
      let newValue = start + 'thru' + end;
      let oldValue = this.valueElement.value;
      if (newValue !== oldValue) {
        this.valueElement.value = newValue;
        this.start.value = start;
        this.end.value = end;
        this.valueElement.trigger('change');
        this.valueElement.trigger('ondaterangechanged');
      }

      this.reset();
    }
  }

  public text(value?: string): string | void {
    let text = this.userFacingElement.querySelector('[data-daterange-text]') as HTMLElement;
    if (text === undefined)
      return text.textContent;

    text.textContent = value;
  }

  public name(): string {
    return this.valueElement.getAttribute('name');
  }

  private getOptionsFromElement(): IDateRangeFieldOptions {
    return {
      daterangeKey: this.valueElement.getAttribute('data-daterange-key'),
      allowsEmpty: StringExtensions.toBoolean(this.valueElement.getAttribute('data-allows-empty'))
    };
  }

  private setUpPopover(): void {
    this.popover = new Popover(this.options.daterangeKey, this.options.allowsEmpty);

    this.popover.onApply((event: IRustigEvent<IApplied>) => {
      let data = event.data;
      this.setValue(data.start, data.end);
      this.popover.close();
      this.userFacingElement.focus();
    });
  }

  private setUpUserFacingElement(): void {
    let togglePopover = () => {
      if (!this.popover.isOpen() && !EditState.isReadonly(this.userFacingElement))
        this.popover.open();
      else
        this.popover.close();
    };

    let tryShowErrorPopover = (channel: any) => {
      if (channel.toString() !== this.options.daterangeKey)
        return;

      let popover = this.userFacingElement.popovers['error'];
      if (popover)
        popover.open();
    };
    //this.userFacingElement.addEventListener('show.rustig.popover', (event: IRustigEvent) => this.reset(event.data.channel));
    $(this.userFacingElement).on('show.rustig.popover', (event: JQueryEventObject, data?: any) => this.reset(data.channel));

    this.userFacingElement.addEventListener('click', (event: MouseEvent) => {
      event.stopPropagation();
      togglePopover();
    });

    //this.userFacingElement.addEventListener('shown.rustig.popover', (event: IRustigEvent) => tryShowErrorPopover(event.data.channel));
    $(this.userFacingElement).on('shown.rustig.popover', (event: JQueryEventObject, data?: any) => tryShowErrorPopover(data.channel));
  }

  private reset(channel?: any) {
    if (channel !== undefined && channel.toString() !== this.options.daterangeKey)
      return;

    let text = this.popover.reset(new NullableDate(this.start.value), new NullableDate(this.end.value));
    this.text(text);
  }

  private setValue(start: NullableDate, end: NullableDate): void {
    this.value(start.toFieldDateString() + 'thru' + end.toFieldDateString());
  }
}


class Popover {

  private key: string;
  private element: HTMLElement;
  private start: Element;
  private end: Element;
  private dropdown: Element;
  private text: string;
  private popover: IPopOverHTMLElement;

  private types: Type[] = [
    Type.All,
    Type.Today,
    Type.Last30Days,
    Type.Last6Months,
    Type.ThisYear,
    Type.LastYear,
    Type.Is,
    Type.Before,
    Type.After,
    Type.Between
  ];

  public constructor(key: string, allowsEmpty: boolean) {
    if (!allowsEmpty)
      this.types.splice(0, 1);

    this.key = key;
    this.element = document.querySelector('daterange-popover-content'.asDataSelector(key));
    this.start = Element.from(this.element.querySelector('[data-start]'));
    this.end = Element.from(this.element.querySelector('[data-end]'));
    this.dropdown = Element.from(this.buildTypeDropdown());
    this.popover = document.querySelector('popover'.asDataSelector(this.key));

    this.wireupEventListeners();
  }

  public close(): void {
    this.popover.popovers[this.key].close();
  }

  public open(): void {
    this.popover.popovers[this.key].open();
  }

  public isOpen(): boolean {
    return this.element.isVisible();
  }

  public onApply(callback: (event: IRustigEvent<IApplied>) => void): void {
    this.element.addEventListener('applied', callback);
  }

  public reset(start: NullableDate, end: NullableDate): string {
    this.start.suppressValueChange(start.value);
    this.end.suppressValueChange(end.value);
    this.dropdown.suppressValueChange('between');
    this.onDateFieldChanged(true);
    return this.text;
  }

  private buildTypeDropdown(): HTMLElement {
    let gridUnit = document.createElement('div');
    gridUnit.classList.add('gu-1');
    this.element.insertBefore(gridUnit, this.element.firstChild);

    let select = document.createElement('select');
    select.classList.add('input-field');
    gridUnit.appendChild(select);
    (this.types).forEach((type) => {
      let option = document.createElement('option');
      option.setAttribute('value', type.value);
      option.textContent = type.value;
      select.appendChild(option);
    });

    let dropDown = new DropDown(select);
    dropDown.flyout.setAttribute('data-noclickoff', this.key);

    return select;
  }

  private wireupEventListeners(): void {
    //this.start.element.addEventListener('change', () => this.onDateFieldChanged());
    this.start.$element.on('change.rustig', () => this.onDateFieldChanged());
    //this.end.element.addEventListener('change', () => this.onDateFieldChanged());
    this.end.$element.on('change.rustig', () => this.onDateFieldChanged());
    //this.dropdown.element.addEventListener('change', () => this.onSelectChanged());
    this.dropdown.$element.on('change.rustig', () => this.onSelectChanged());

    let applyButton = this.element.querySelector('[data-daterange-apply]');
    applyButton.addEventListener('click', () => {
      this.reset(new NullableDate(this.start.value()), new NullableDate(this.end.value()));

      let data = {
        start: new NullableDate(this.start.value()),
        end: new NullableDate(this.end.value()),
        text: this.text
      };
      this.element.trigger<IApplied>('applied', data);
    });

    //this.popover.addEventListener('shown.rustig.popover', () => this.dropdown.userFacingElement.focus());
    $(this.popover).on('shown.rustig.popover', () => this.dropdown.userFacingElement.focus());
  }

  private onDateFieldChanged(canSwichTypeFromBetween: boolean = false): void {
    let typeValue = this.dropdown.value();
    let start = new NullableDate(this.start.value());
    let end = new NullableDate(this.end.value());

    let type;
    if (canSwichTypeFromBetween || typeValue !== 'between')
      type = _.find(this.types, x => x.predicate(typeValue, start, end));
    else
      type = _.find(this.types, x => x.value === 'between');

    this.dropdown.suppressValueChange(type.value);
    this.update(type.build(start, end));
  }

  private onSelectChanged(): void {
    let typeValue = this.dropdown.value();
    let type = _.find(this.types, x => x.value == typeValue);
    this.update(type.build(new NullableDate(this.start.value()), new NullableDate(this.end.value())));
  }

  private update(dateRange: IDateRange): void {
    this.start.suppressValueChange(dateRange.start.value);
    ToggleShow.execute(this.start.element.closest('.gu-1') as HTMLElement, dateRange.shouldShowStart);

    this.end.suppressValueChange(dateRange.end.value);
    ToggleShow.execute(this.end.element.closest('.gu-1') as HTMLElement, dateRange.shouldShowEnd);

    this.text = dateRange.text;
  }
}

interface IApplied {
  start: NullableDate;
  end: NullableDate;
  text: string;
}

interface IDateRange {
  start: NullableDate;
  end: NullableDate;
  text: string;
  shouldShowStart: boolean;
  shouldShowEnd: boolean;
}

class Type {
  value: string;
  predicate: (type: string, start: NullableDate, end: NullableDate) => boolean;
  build: (start: NullableDate, end: NullableDate) => IDateRange;

  static get Today(): Type {
    let today = new NullableDate(new Date());
    return {
      value: 'today',
      predicate: (type: string, start: NullableDate, end: NullableDate) => start.equals(today) && start.equals(end),
      build: (start: NullableDate, end: NullableDate) => {
        return {
          start: today,
          end: today,
          text: 'today',
          shouldShowStart: true,
          shouldShowEnd: false
        };
      }
    };
  }

  static get Is(): Type {
    let today = new Date();
    return {
      value: 'is',
      predicate: (type: string, start: NullableDate, end: NullableDate) => {
        let typeWasTodayAndDateChanged = type === 'today' && (!start.equals(today) || !end.equals(today));
        return typeWasTodayAndDateChanged || type === 'is' || start.equals(end) || (!start.hasValue && !end.hasValue);
      },
      build: (start: NullableDate, end: NullableDate) => {
        return {
          start: start.hasValue ? start : end,
          end: start.hasValue ? start : end,
          text: start.toFieldDateString(),
          shouldShowStart: true,
          shouldShowEnd: false
        };
      }
    };
  }

  static get Last30Days(): Type {
    let today = new Date();
    return {
      value: 'last 30 days',
      predicate: (type: string, start: NullableDate, end: NullableDate) => end.equals(today) && start.equals(today.addDays(-30)),
      build: (start: NullableDate, end: NullableDate) => {
        return {
          start: new NullableDate(today.addDays(-30)),
          end: new NullableDate(today),
          text: 'last 30 days',
          shouldShowStart: true,
          shouldShowEnd: true
        };
      }
    };
  }

  static get Last6Months(): Type {
    let today = new Date();
    return {
      value: 'last 6 months',
      predicate: (type: string, start: NullableDate, end: NullableDate) => end.equals(today) && start.equals(today.addMonths(-6)),
      build: (start: NullableDate, end: NullableDate) => {
        return {
          start: new NullableDate(today.addMonths(-6)),
            end: new NullableDate(today),
          text: 'last 6 months',
          shouldShowStart: true,
          shouldShowEnd: true
        };
      }
    };
  }

  static get ThisYear(): Type {
    let januaryFirst = new NullableDate(new Date(new Date().getFullYear(), 0, 1));
    let decemberThirtyFirst = new NullableDate(new Date(new Date().getFullYear(), 11, 31));
    return {
      value: 'this year',
      predicate: (type: string, start: NullableDate, end: NullableDate) => start.equals(januaryFirst) && end.equals(decemberThirtyFirst),
      build: (start: NullableDate, end: NullableDate) => {
        return {
          start: januaryFirst,
          end: decemberThirtyFirst,
          text: 'this year',
          shouldShowStart: true,
          shouldShowEnd: true
        };
      }
    }
  }

  static get LastYear(): Type {
    let januaryFirst = new NullableDate(new Date(new Date().getFullYear() - 1, 0, 1));
    let decemberThirtyFirst = new NullableDate(new Date(new Date().getFullYear() - 1, 11, 31));
    return {
      value: 'last year',
      predicate: (type: string, start: NullableDate, end: NullableDate) => start.equals(januaryFirst) && end.equals(decemberThirtyFirst),
      build: (start: NullableDate, end: NullableDate) => {
        return {
          start: januaryFirst,
          end: decemberThirtyFirst,
          text: 'last year',
          shouldShowStart: true,
          shouldShowEnd: true
        };
      }
    };
  }

  static get Between(): Type {
    return {
      value: 'between',
      predicate: (type: string, start: NullableDate, end: NullableDate) => true,
      build: (start: NullableDate, end: NullableDate) => {
        return {
          start: start,
          end: end,
          text: (start.hasValue ? start.toFieldDateString() : '?') + ' - ' + (end.hasValue ? end.toFieldDateString() : '?'),
          shouldShowStart: true,
          shouldShowEnd: true
        };
      }
    };
  }

  static get All(): Type {
    return {
      value: '',
      predicate: (type: string, start: NullableDate, end: NullableDate) => false,
      build: (start: NullableDate, end: NullableDate) => {
        return {
          start: NullableDate.empty(),
          end: NullableDate.empty(),
          text: '',
          shouldShowStart: false,
          shouldShowEnd: false
        };
      }
    };
  }

  static get Before(): Type {
    return {
      value: 'before',
      predicate: (type: string, start: NullableDate, end: NullableDate) => !start.hasValue && end.hasValue,
      build: (start: NullableDate, end: NullableDate) => {
        return {
          start: NullableDate.empty(),
          end: end,
          text: 'before ' + end.toFieldDateString(),
          shouldShowStart: false,
          shouldShowEnd: true
        };
      }
    };
  }

  static get After(): Type {
    return {
      value: 'after',
      predicate: (type: string, start: NullableDate, end: NullableDate) => start.hasValue && !end.hasValue,
      build: (start: NullableDate, end: NullableDate) => {
        return {
          start: start,
          end: NullableDate.empty(),
          text: 'after ' + start.toFieldDateString(),
          shouldShowStart: true,
          shouldShowEnd: false
        };
      }
    };
  }
}

class NullableDate {
  private _value: Date;

  public get hasValue(): boolean { return this._value !== null; }
  public get value(): Date { return this._value; }

  public constructor(date: Date | string) {
    if (!date)
      this._value = null;
    else if (typeof date === 'string')
      this._value = new Date(date);
    else
      this._value = date;
  }

  public toFieldDateString(): string {
    return this.hasValue ? this.value.toFieldDateString() : '';
  }

  public equals(date: Date | NullableDate): boolean {
    if (!this.hasValue)
      return false;

    if (date instanceof Date)
      return this.value.equals(date);
    else
      return date.hasValue ? this.value.equals(date.value) : false;
  }

  public static empty(): NullableDate {
    return new NullableDate(null);
  }
}

export interface IDateRangeElement extends HTMLInputElement {
  dateRangeField: DateRangeField;
}

export interface IDateRangeFieldOptions {
  daterangeKey: string;
  allowsEmpty?: boolean;
}

export default DateRangeField;

